import {Fragment, useState} from 'react'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {Check} from 'lucide-react'
import {formatTimestamp} from '@/lib/date-utils'
import {ConstraintEvalStatus, ConstraintSeverityBadge} from '@/types/constraint'
import {ViolationStatus, type ViolationSummarySubjectRef} from '@/types/violation'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {ViolationControllerListEndpoint} from '@/types/endpoint-contracts'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Checkbox} from '@/components/ui/checkbox'
import type {CheckedState} from '@radix-ui/react-checkbox'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {TextLink} from '@/components/shared/TextLink'

function subjectRoute(subject: ViolationSummarySubjectRef): string | null {
    switch (subject.entityTable) {
        case 'parties':
            return routes.identityManagement.parties.detail(subject.entityId)
        case 'identities':
            return routes.identityManagement.identities.detail(subject.entityId)
        case 'function_groups':
            return routes.accessGovernance.functionGroups.detail(subject.entityId)
        default:
            return null
    }
}

function subjectLabel(subject: ViolationSummarySubjectRef): string {
    if (subject.label) return subject.label
    return `${subject.entityTable}:${subject.entityId}`
}

function statusBadgeVariant(status: string): BadgeVariant {
    switch (status) {
        case ViolationStatus.OPEN: return BadgeVariant.DESTRUCTIVE
        case ViolationStatus.ACKED: return BadgeVariant.WARNING
        case ViolationStatus.RESOLVED: return BadgeVariant.SUCCESS
        default: return BadgeVariant.OUTLINE
    }
}

function statusLabel(status: string, i18n: ReturnType<typeof useI18n>): string {
    switch (status) {
        case ViolationStatus.OPEN: return i18n.schemaViolations.statusOpen
        case ViolationStatus.ACKED: return i18n.schemaViolations.statusAcked
        case ViolationStatus.RESOLVED: return i18n.schemaViolations.statusResolved
        default: return status
    }
}

export function SchemaViolations() {
    const i18n = useI18n()
    const services = useServices()
    const queryClient = useQueryClient()
    usePageTitle(i18n.schemaViolations.pageTitle)
    const {hasPermission} = useAuth()
    const canAck = hasPermission(permissions.violations.ack)

    const {
        page, pageSize,
        setPage, setPageSize, pageSizeOptions,
    } = useServerPagination({
        endpoint: ViolationControllerListEndpoint,
        sortable: false,
        defaultSortBy: ViolationControllerListEndpoint.sort.DETECTED_AT,
    })

    const {data: violationsData, isLoading} = useQuery({
        queryKey: ['schema-violations', page, pageSize],
        queryFn: () => services.violations.list({page, size: pageSize}),
    })

    const violations = violationsData?.content || []
    const totalElements = violationsData?.totalElements || 0
    const totalPages = violationsData?.totalPages || 0

    const [selected, setSelected] = useState<Set<string>>(new Set())

    const toggleOne = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }
    const toggleAll = () => {
        if (selected.size === violations.length && violations.length > 0) setSelected(new Set())
        else setSelected(new Set(violations.map((v) => v.id)))
    }

    const invalidateAll = () => {
        queryClient.invalidateQueries({queryKey: ['schema-violations']})
        queryClient.invalidateQueries({queryKey: ['schema-policies']})
        queryClient.invalidateQueries({queryKey: ['violation-summary']})
    }

    const bulkAckMutation = useMutation({
        mutationFn: () => services.violations.bulkAck({ids: Array.from(selected)}),
        onSuccess: () => {
            invalidateAll()
            setSelected(new Set())
        },
    })

    const rowAckMutation = useMutation({
        mutationFn: (id: string) => services.violations.ack(id, {}),
        onSuccess: () => {
            invalidateAll()
        },
    })

    const headerCheckboxState: CheckedState = selected.size === 0
        ? false
        : selected.size === violations.length ? true : 'indeterminate'

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.violation}
            title={i18n.schemaViolations.allViolations}
            count={totalElements}
            description={i18n.schemaViolations.violationsCount({count: totalElements})}
            data-testid="schema-violations-list"
            actions={
                selected.size > 0 && canAck ? (
                    <Button
                        onClick={() => bulkAckMutation.mutate()}
                        disabled={bulkAckMutation.isPending}
                        data-testid="btn-bulk-ack"
                    >
                        <Icon icon={Check} size="sm" className="mr-2"/>
                        {i18n.schemaViolations.bulkAck} ({selected.size})
                    </Button>
                ) : undefined
            }
            pagination={totalPages > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    totalItems={totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-10" data-testid="col-select">
                            <Checkbox
                                checked={headerCheckboxState}
                                onCheckedChange={toggleAll}
                                disabled={violations.length === 0}
                                data-testid="checkbox-toggle-all"
                                aria-label={i18n.schemaViolations.toggleAll}
                            />
                        </TableHead>
                        <TableHead data-testid="col-policy">{i18n.schemaViolations.policy}</TableHead>
                        <TableHead data-testid="col-detected-at">{i18n.schemaViolations.detectedAt}</TableHead>
                        <TableHead data-testid="col-severity">{i18n.schemaViolations.severity}</TableHead>
                        <TableHead data-testid="col-status">{i18n.schemaViolations.status}</TableHead>
                        <TableHead data-testid="col-message">{i18n.schemaViolations.message}</TableHead>
                        <TableHead data-testid="col-actions">{i18n.common.actions}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && (
                        <TableEmptyState
                            colSpan={7}
                            hasFilters={false}
                            matchMessage={i18n.schemaViolations.loadingViolations}
                            emptyMessage={i18n.schemaViolations.loadingViolations}
                        />
                    )}
                    {!isLoading && violations.map((v) => (
                        <TableRow
                            key={v.id}
                            data-testid={`violation-row-${v.id}`}
                            data-readonly
                        >
                            <TableCell>
                                <Checkbox
                                    checked={selected.has(v.id)}
                                    onCheckedChange={() => toggleOne(v.id)}
                                    data-testid={`checkbox-violation-${v.id}`}
                                    aria-label={i18n.schemaViolations.selectRow}
                                />
                            </TableCell>
                            <TableCell className="font-medium">
                                <InlineRow gap="xs" align="center">
                                    <span>{v.constraintName}</span>
                                    {v.evalStatus === ConstraintEvalStatus.STALE && (
                                        <Badge
                                            variant={BadgeVariant.OUTLINE}
                                            data-testid={`badge-reference-broken-${v.id}`}
                                        >
                                            {i18n.schemaViolations.referenceBroken}
                                        </Badge>
                                    )}
                                </InlineRow>
                            </TableCell>
                            <TableCell>{formatTimestamp(v.detectedAt)}</TableCell>
                            <TableCell>
                                <Badge variant={ConstraintSeverityBadge.fromCode(v.severity).variant}>
                                    {v.severity}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant={statusBadgeVariant(v.status)}>
                                    {statusLabel(v.status, i18n)}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                                <div>{v.message}</div>
                                {v.subjects.length > 0 && (
                                    <MutedText size="sm" data-testid={`violation-subjects-${v.id}`}>
                                        {v.subjects.map((subject, idx) => {
                                            const route = subjectRoute(subject)
                                            const label = subjectLabel(subject)
                                            return (
                                                <Fragment key={`${subject.entityTable}:${subject.entityId}`}>
                                                    {idx > 0 && ', '}
                                                    {route ? (
                                                        <TextLink
                                                            to={route}
                                                            testId={`subject-link-${v.id}-${idx}`}
                                                            stopPropagation
                                                        >
                                                            {label}
                                                        </TextLink>
                                                    ) : (
                                                        <span>{label}</span>
                                                    )}
                                                </Fragment>
                                            )
                                        })}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                {canAck && v.status === ViolationStatus.OPEN && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => rowAckMutation.mutate(v.id)}
                                        disabled={rowAckMutation.isPending}
                                        data-testid={`btn-row-ack-${v.id}`}
                                    >
                                        {i18n.schemaViolations.actionAck}
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {!isLoading && violations.length === 0 && (
                        <TableEmptyState
                            colSpan={7}
                            hasFilters={false}
                            matchMessage={i18n.schemaViolations.noViolationsFound}
                            emptyMessage={i18n.schemaViolations.noViolationsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
